/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useEffect, useMemo, Children, useState, lazy, Suspense } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { B100, N0, N20, N40, R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import UserPicker, {
	type ExternalUser,
	type Group,
	type OptionData,
	type UserPickerProps,
	type Value,
	isEmail,
	isExternalUser,
} from '@atlaskit/user-picker';

import { useCoreInvitesJiraProjectContext } from '../../experiments/coreInvitesInJiraProjects/CoreInvitesJiraProjectContext';
import { USER_PICKER_ID, type InviteeListId, TEAM_OR_USER_PICKER_ID } from '../../analytics';
import { useThirdPartyState, ThirdPartyConnectedTo, UpdateConnection } from '../ThirdParty';
import {
	mergeAndSearchExternalTeamsOrUsers,
	mergeAndSearchExternalUsers,
} from '../ThirdParty/utils';
import { useInvitePeopleContext } from '../InvitePeople';
import { messages } from '../i18n';
import { type ExternalTeam, type ExternalTeamOrUser, ScopeState, ZIndex } from '../../types';
import { extractDomainFromEmail, removeZeroWidthCharacters } from '../../utils';
import { useAnalytics } from '../../analytics';
import { useInviteThirdPartyTeams } from '../../experiments/inviteThirdPartyTeams/useInviteThirdPartyTeams';
import TeamOrUserPicker from '../TeamOrUserPicker/TeamOrUserPicker';
import { OPTION_TYPE_CUSTOM } from '../../experiments/constants';
import { PLATFORM_SLACK_SERVICE } from '../ThirdParty/constants';
import { useViralSettingsContext } from '../ViralSettings';
import {
	useIsStickyExperimentEnabled,
	useStickyExperimentStyles,
} from '../../experiments/peuExpansionStickyEmailOption';

import { useDebounce } from './useDebounce';
import {
	determineValidationState,
	validateEmail,
	type ValidationState,
	hasError,
} from './validate';
import { InviteeListAnalytics } from './InviteeListAnalytics';
import { useInviteeListStateContext, getEmailAndIdArrays } from './InviteeListContext';

type LoadOptions = (searchText: string | undefined) => Promise<OptionData[]>;

const LazySlackTeamsSpotlight = lazy(
	() => import(/* webpackChunkName: "async-slack-teams-spotlight" */ '../SlackTeamsSpotlight'),
);

const styles = cssMap({
	fieldWrapper: {
		position: 'relative',
	},
	label: {
		display: 'block',
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		color: token('color.text.subtlest', '#626F86'),
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.050', '4px'),
	},
});

export interface OwnProps {
	restrictedDomains?: string[];
	maxEmails?: number;
	onInviteeListChange?: (emails: string[], validEmails: string[]) => void;
	showEmptyError?: boolean;
	autoFocus?: boolean;
}

const UserPickerError = ({
	validationList,
	isEmptyInput,
	maxEmails,
}: {
	validationList: ValidationState[];
	isEmptyInput: boolean;
	maxEmails: number;
}) => {
	return (
		<div data-testid="testId-invite-people-invitee-list-error-messages">
			{Children.toArray(
				validationList.map(
					(validationState) =>
						validationState.validationState === 'error' &&
						validationState.errorMessage && (
							<ErrorMessage>
								<FormattedMessage {...validationState.errorMessage} values={{ maxEmails }} />
							</ErrorMessage>
						),
				),
			)}
			{isEmptyInput && (
				<ErrorMessage>
					<FormattedMessage {...messages.inviteeListPickerNote} />
				</ErrorMessage>
			)}
		</div>
	);
};

const InviteeList: React.FC<OwnProps> = ({
	maxEmails = 10,
	onInviteeListChange = () => {},
	showEmptyError = false,
	autoFocus = false,
}) => {
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);
	const analytics = useAnalytics(InviteeListAnalytics);
	const { isCoreInvitesInJiraProjectEnabled } = useCoreInvitesJiraProjectContext();
	const { onSlackConnected } = useInviteThirdPartyTeams();
	const {
		allowEmail,
		selectedTeam,
		setSelectedTeam,
		setUserPickerOptions,
		userPickerOptions,
		experimental_userPickerByline,
	} = useInviteeListStateContext();
	const { onInviteeListFocus } = useViralSettingsContext();

	const {
		queryThirdParty,
		querySlackChannels,
		integrations,
		updateIntegrations,
		isConnected: thirdPartyConnected,
	} = useThirdPartyState();
	const {
		cloudId,
		invitePromptOnLinkV3Cohort,
		inviteeProvider,
		loggedInEmailDomain,
		productId,
		userEmail,
	} = useInvitePeopleContext();

	const [spotlightHidden, setSpotlightHidden] = useState(true);

	useEffect(() => {
		if (onSlackConnected) {
			setSpotlightHidden(false);
		}
	}, [onSlackConnected]);

	const [hasValidationError, validationList] = useMemo(() => {
		const { emails, ids } = getEmailAndIdArrays(userPickerOptions);
		const validationList = determineValidationState(
			emails,
			ids,
			maxEmails,
			userEmail,
			isCoreInvitesInJiraProjectEnabled,
		);
		return [hasError(validationList), validationList];
	}, [maxEmails, userPickerOptions, userEmail, isCoreInvitesInJiraProjectEnabled]);

	// This is used to make the "Enter up to 10 email addresses" an error if
	// the field is empty; EXCEPT it won't show until the form has been submitted
	const isEmptyInput = userPickerOptions.length === 0 && showEmptyError;

	const itpt = useInviteThirdPartyTeams();
	const onFocusEventWithAnalytics = useCallback(
		(pickerId: InviteeListId) => () => {
			analytics.inviteeListFocused(pickerId);
			onInviteeListFocus();
		},
		[analytics, onInviteeListFocus],
	);
	const userPickerTypedDebounce = useDebounce(2000);

	const getNewEmail = (prevEmails: string[], emails: string[]): string => {
		const newEmails = emails.filter((email: string) => !prevEmails.includes(email));
		return newEmails.shift() || '';
	};

	const onUserSelectionChange = useCallback(
		(value: Value) => {
			// picking a team
			if (itpt.isEnabled && Array.isArray(value) && value.length > 0) {
				const lastOption = value[value.length - 1];
				if (lastOption.type === OPTION_TYPE_CUSTOM) {
					setSelectedTeam(lastOption);
					analytics.slackChannelClicked(lastOption.id);
					return;
				}
			}

			let values: OptionData[] = [];
			if (Array.isArray(value)) {
				values = value;
			} else if (value) {
				values = [value];
			}

			for (const value of values) {
				if (isEmail(value)) {
					value.name = removeZeroWidthCharacters(value.name);
					value.id = removeZeroWidthCharacters(value.id);
				}
				if (isExternalUser(value) && value.sources.includes('slack')) {
					analytics.slackUserSelected();
				}
			}

			const { emails } = getEmailAndIdArrays(values);

			setUserPickerOptions((prevEmails) => {
				// Only fire analytics event if an email is added
				const { emails: previousEmailsStrings } = getEmailAndIdArrays(prevEmails);
				prevEmails.length < values.length &&
					analytics.inviteeListItemAdded(USER_PICKER_ID, {
						domains: [extractDomainFromEmail(getNewEmail(previousEmailsStrings, emails)) || ''],
					});
				return values;
			});
		},
		[itpt.isEnabled, setUserPickerOptions, setSelectedTeam, analytics],
	);

	// Validator and call onInviteeListChange
	useEffect(() => {
		const { emails, ids } = getEmailAndIdArrays(userPickerOptions);

		if (emails.length === 0 && ids.length === 0) {
			onInviteeListChange([], []);
			return;
		}
		const selectOptions = [...emails, ...ids];
		// Finally, send back all entered email addresses to the parent component
		onInviteeListChange(
			selectOptions,
			// valid emails
			emails.filter((email) => validateEmail(email) === 'VALID'),
		);
	}, [userPickerOptions, maxEmails, onInviteeListChange, userEmail]);

	const loadOptionsBase = useCallback<LoadOptions>(
		async (searchText) => {
			if (!searchText || searchText.trim().length === 0) {
				return [];
			}

			const onInviteeLoadOptionsPromise =
				inviteeProvider?.onInviteeLoadOptions({ searchText }) ?? Promise.resolve([]);
			const queryThirdPartyPromise = queryThirdParty(searchText);

			const [thirdPartySearch, inviteeLoadOptionsSearch] = await Promise.all([
				queryThirdPartyPromise,
				onInviteeLoadOptionsPromise,
			]);

			const providedAdditionalUsers: ExternalUser[] = [];
			const groups: Group[] = [];

			inviteeLoadOptionsSearch.forEach((item) => {
				if (item.type === 'group') {
					groups.push(item);
				} else {
					providedAdditionalUsers.push(item);
				}
			});

			let mergedExternalUsers: ExternalUser[] = thirdPartySearch;
			if (providedAdditionalUsers.length > 0) {
				mergedExternalUsers = mergeAndSearchExternalUsers({
					providedAdditionalUsers,
					searchText,
					connectedThirdPartyUsers: thirdPartySearch,
				});
			}

			const options = [...groups, ...mergedExternalUsers];
			const { onInviteeResultsMerged } = inviteeProvider || {};

			if (onInviteeResultsMerged) {
				return onInviteeResultsMerged(options);
			}

			return options;
		},
		[inviteeProvider, queryThirdParty],
	);

	const withTypedEvent = useCallback(
		(loadOptions: LoadOptions): LoadOptions =>
			async (searchText) => {
				const options = await loadOptions(searchText);
				if (searchText !== undefined) {
					userPickerTypedDebounce(() => {
						analytics.userPickerTyped({
							queryLength: searchText.length,
							results: options.map((option) => ({
								type: option.type ?? null,
								// Note that sources from cache will only be added if option
								// is loaded from org-people-picker
								sources: (option as ExternalUser).sources,
							})),
						});
					});
				}
				return options;
			},
		[analytics, userPickerTypedDebounce],
	);

	const loadOptions = useMemo(
		() => withTypedEvent(loadOptionsBase),
		[loadOptionsBase, withTypedEvent],
	);

	const loadOptionsWithTeams = useMemo(
		() =>
			withTypedEvent(async (searchText) => {
				let teamsOrUsers: ExternalTeamOrUser[];
				let slackChannelsSearch: ExternalTeam[];
				const groups: Group[] = [];

				if (!searchText || searchText.trim().length === 0) {
					return [];
				}
				let optionsSearch: OptionData[];
				[optionsSearch, slackChannelsSearch] = await Promise.all([
					loadOptionsBase(searchText),
					querySlackChannels(searchText),
				]);

				const usersSearch: ExternalUser[] = [];

				optionsSearch.forEach((option: OptionData) => {
					if (option.type === 'user' && (option as ExternalUser).sources) {
						usersSearch.push(option as ExternalUser);
					} else if (option.type === 'group') {
						groups.push(option as Group);
					}
				});

				teamsOrUsers = mergeAndSearchExternalTeamsOrUsers(
					searchText,
					usersSearch,
					slackChannelsSearch,
				);

				if (slackChannelsSearch.length > 0) {
					const teamIds = teamsOrUsers.reduce((ids: string[], option: OptionData) => {
						if (option.type === OPTION_TYPE_CUSTOM) {
							ids.push(option.id);
						}
						return ids;
					}, []);

					analytics.teamOrUserPickerViewed({
						teamIds,
					});
				}

				const { onInviteeResultsMerged } = inviteeProvider || {};
				if (onInviteeResultsMerged) {
					return onInviteeResultsMerged([...groups, ...teamsOrUsers]);
				}
				return [...groups, ...teamsOrUsers];
			}),
		[withTypedEvent, inviteeProvider, loadOptionsBase, querySlackChannels, analytics],
	);

	const isStickyEmail = useIsStickyExperimentEnabled(allowEmail);

	const peuExperimentStickyStyles = useStickyExperimentStyles(userPickerOptions);

	const customStyles = useMemo(() => {
		return {
			control: (css: any, state: any) => {
				const borderColor =
					hasValidationError || isEmptyInput
						? token('color.border.danger', R400)
						: state.isFocused
							? token('color.border.focused', B100)
							: token('color.border', N40);

				return {
					...css,
					borderColor,
					backgroundColor: state.isFocused
						? token('color.background.input.hovered', N20)
						: token('color.background.input', N0),
					':hover': {
						...css[':hover'],
						backgroundColor: token('color.background.input.hovered', N20),
						borderColor,
					},
				};
			},
			menuPortal: (provided: any) => ({ ...provided, zIndex: ZIndex.FLOATING }),
			...(isStickyEmail && peuExperimentStickyStyles),
		};
	}, [isEmptyInput, hasValidationError, isStickyEmail, peuExperimentStickyStyles]);

	const commonUserPickerProps = useMemo(() => {
		const onInputChange = (query?: string) => {
			const trimmedValue = query?.trimStart();
			setInputValue(trimmedValue);
		};
		const pickerThirdPartyTeamsPlaceholder = (
			<FormattedMessage {...messages.inviteeListUserPickerEmailPlaceholderThirdPartyTeams} />
		);
		const pickerPlaceholder = <FormattedMessage {...messages.inviteeListUserPickerPlaceholder} />;
		const props: Omit<
			UserPickerProps,
			'fieldId' | 'inputId' | 'ariaLabelledBy' | 'onFocus' | 'loadOptions'
		> = {
			ariaLive: 'polite',
			autoFocus,
			onChange: onUserSelectionChange,
			noOptionsMessage: () => null,
			placeholder:
				itpt.isEnabled && integrations.has(PLATFORM_SLACK_SERVICE)
					? pickerThirdPartyTeamsPlaceholder
					: pickerPlaceholder,
			width: '100%',
			value: userPickerOptions,
			isValidEmail: validateEmail,
			isClearable: true,
			isMulti: true,
			allowEmail,
			suggestEmailsForDomain: loggedInEmailDomain,
			styles: customStyles,
			menuPortalTarget: document.body,
			menuPosition: 'absolute',
			onInputChange,
			search: inputValue,
			required: true,
		};
		return props;
	}, [
		allowEmail,
		autoFocus,
		customStyles,
		integrations,
		itpt.isEnabled,
		loggedInEmailDomain,
		onUserSelectionChange,
		userPickerOptions,
		inputValue,
	]);

	const shouldLoadOptions = !!inviteeProvider?.onInviteeLoadOptions || integrations.size > 0;

	const renderPicker = useCallback(
		(describedByFieldId?: string) => {
			if (itpt.isEnabled && cloudId) {
				return (
					<Suspense fallback={null}>
						<LazySlackTeamsSpotlight
							productId={productId}
							spotlightHidden={spotlightHidden}
							dismissSpotlight={() => setSpotlightHidden(true)}
						>
							<TeamOrUserPicker
								tenantId={cloudId}
								product={productId}
								fieldId="invite-team-user-picker"
								inputId="invite-team-user-picker"
								ariaLabelledBy="invite-people-title"
								aria-describedby={describedByFieldId}
								onFocus={onFocusEventWithAnalytics(TEAM_OR_USER_PICKER_ID)}
								loadOptions={shouldLoadOptions ? loadOptionsWithTeams : undefined}
								selectedTeam={selectedTeam}
								setSelectedTeam={setSelectedTeam}
								// Calculated using the following measurements
								//
								// 6 (rows) *
								// 28px (pill height (24px) + margin top (2px) + margin bottom (2px)) -
								// 14px (half of the above value, since we want to show half of the top pill to indicate there's more pills to scroll through) +
								// 6px (user picker bottom padding) +
								// 28px (height of the "add more people..." placeholder (20px) + margin top (4px) + margin bottom (4px))
								//
								// This is done so that we can show 5 rows of pills + half a row of pills when there are too many pills in the user
								// picker
								maxPickerHeight={6 * 28 - 14 + 6 + 28}
								{...commonUserPickerProps}
							/>
						</LazySlackTeamsSpotlight>
						{!!invitePromptOnLinkV3Cohort && (
							<Text as="span" size="small" color="color.text.subtlest">
								{experimental_userPickerByline}
							</Text>
						)}
					</Suspense>
				);
			}

			return (
				<>
					<UserPicker
						fieldId="invite-user-picker"
						inputId="invite-user-picker"
						ariaLabelledBy="invite-people-title"
						aria-describedby={describedByFieldId}
						onFocus={onFocusEventWithAnalytics(USER_PICKER_ID)}
						loadOptions={shouldLoadOptions ? loadOptions : undefined}
						{...commonUserPickerProps}
					/>
					{!!invitePromptOnLinkV3Cohort && (
						<Text as="span" size="small" color="color.text.subtlest">
							{experimental_userPickerByline}
						</Text>
					)}
				</>
			);
		},
		[
			itpt.isEnabled,
			cloudId,
			onFocusEventWithAnalytics,
			shouldLoadOptions,
			loadOptions,
			commonUserPickerProps,
			productId,
			spotlightHidden,
			loadOptionsWithTeams,
			selectedTeam,
			setSelectedTeam,
			invitePromptOnLinkV3Cohort,
			experimental_userPickerByline,
		],
	);

	return (
		<Field name="invite-people-invitee-list">
			{(formProps) => (
				<Box testId="testId-invite-people-invitee-list" xcss={styles.fieldWrapper}>
					<Box
						as="label"
						htmlFor={itpt.isEnabled ? 'invite-team-user-picker' : 'invite-user-picker'}
						xcss={styles.label}
					>
						{itpt.isEnabled ? (
							<FormattedMessage {...messages.inviteeListLabelThirdPartyTeams} />
						) : (
							<FormattedMessage {...messages.inviteeListLabelThirdParty} />
						)}
						<RequiredAsterisk />
					</Box>
					{renderPicker(`${formProps.fieldProps.id}-error`)}
					<UserPickerError
						validationList={validationList}
						isEmptyInput={isEmptyInput}
						maxEmails={maxEmails}
					/>

					{thirdPartyConnected && <ThirdPartyConnectedTo />}

					{!!updateIntegrations.partialScope.size && (
						<UpdateConnection scope={ScopeState.PARTIAL_SCOPE} />
					)}

					{thirdPartyConnected && !!updateIntegrations.noScope.size && (
						<UpdateConnection scope={ScopeState.NO_SCOPE} />
					)}
				</Box>
			)}
		</Field>
	);
};

export default InviteeList;
